import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// ✅ NEW: Helper function to format order IDs for the invoice
const formatOrderId = (id) => {
  if (!id) return "Manual Walk-in";
  return "OD" + String(id).padStart(4, "0");
};

export const generateInvoice = (rawData) => {
  if (!rawData) return;

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // ==========================================
  // HIGH-LEVEL PINCODE & STATE DETECTION
  // ==========================================
  let rawState =
    rawData.place_of_supply ||
    rawData.shipping_state ||
    rawData.state ||
    "West Bengal";
  let isInterState = !String(rawState)
    .toLowerCase()
    .replace(/\s+/g, "")
    .includes("westbengal");

  // Search the address/pincode fields for a valid 6-digit Indian PIN code
  const addressStringForPin = `${rawData.billing_address || ""} ${rawData.shipping_pincode || ""} ${rawData.pincode || ""}`;
  const pinMatch = addressStringForPin.match(/\b\d{6}\b/);

  if (pinMatch) {
    const pinCode = pinMatch[0];
    const pinPrefix = parseInt(pinCode.substring(0, 2), 10);

    // West Bengal Pincodes range from 70xxxx to 74xxxx
    if (pinPrefix >= 70 && pinPrefix <= 74) {
      isInterState = false;
      rawState = "West Bengal"; // Force correct label
    } else {
      isInterState = true;
      // If state was wrongly left as WB but pin is outside, update the label on the PDF
      if (String(rawState).toLowerCase().includes("west bengal")) {
        rawState = "Out of State (Auto-detected via PIN)";
      }
    }
  }

  const determinedPlaceOfSupply = rawState;

  // ================= BORDER =================
  doc.setDrawColor(15, 23, 42);
  doc.setLineWidth(0.5);
  doc.rect(7, 7, pageWidth - 14, pageHeight - 14);

  // ==========================================
  // SMART COMPATIBILITY LAYER
  // ==========================================
  let invoiceRecord = {};

  if (rawData.invoice_type) {
    invoiceRecord = { ...rawData };
    invoiceRecord.items =
      typeof invoiceRecord.items === "string"
        ? JSON.parse(invoiceRecord.items)
        : invoiceRecord.items;
  } else {
    let totalTaxable = 0;
    let totalCGST = 0;
    let totalSGST = 0;
    let totalIGST = 0;

    const mappedItems = (rawData.OrderItems || []).map((item) => {
      const qty = item.quantity;
      const priceIncl = Number(item.unit_price);
      const gstRate = Number(item.gst_rate || item.Inventory?.gst_rate || 18);
      const taxable = (priceIncl * qty) / (1 + gstRate / 100);
      const gstAmt = priceIncl * qty - taxable;

      let cgstAmt = 0;
      let sgstAmt = 0;
      let igstAmt = 0;

      // Split taxes based on our new bulletproof isInterState flag
      if (isInterState) {
        igstAmt = gstAmt;
        totalIGST += igstAmt;
      } else {
        cgstAmt = gstAmt / 2;
        sgstAmt = gstAmt / 2;
        totalCGST += cgstAmt;
        totalSGST += sgstAmt;
      }

      totalTaxable += taxable;

      return {
        name: item.Inventory?.name || "Product",
        sku: item.Inventory?.sku || "N/A",
        hsn: item.hsn_code || item.Inventory?.hsn_code || "N/A",
        qty: qty,
        unit_price: (taxable / qty).toFixed(2),
        gst_rate: gstRate,
        taxable: taxable.toFixed(2),
        cgst: cgstAmt.toFixed(2),
        sgst: sgstAmt.toFixed(2),
        igst: igstAmt.toFixed(2),
        total: (priceIncl * qty).toFixed(2),
      };
    });

    const shippingFee = Number(rawData.shipping_fee || 300);
    const shipTaxable = shippingFee / 1.18;
    const shipGst = shippingFee - shipTaxable;

    totalTaxable += shipTaxable;

    if (isInterState) {
      totalIGST += shipGst;
    } else {
      totalCGST += shipGst / 2;
      totalSGST += shipGst / 2;
    }

    const fullAddressCompiled = [
      rawData.shipping_line1,
      rawData.shipping_line2,
      rawData.shipping_city,
      rawData.shipping_state,
      rawData.shipping_pincode || rawData.shipping_zip || rawData.pincode,
    ]
      .filter(Boolean)
      .join(", ");

    invoiceRecord = {
      // ✅ UPDATED: Formatted invoice number
      invoice_number: `ZYN/DRAFT/${formatOrderId(rawData.order_id)}`,
      order_id: rawData.order_id,
      invoice_date: rawData.createdAt || new Date(),
      place_of_supply: determinedPlaceOfSupply,
      customer_name:
        rawData.shipping_name || rawData.User?.first_name || "Customer",
      customer_phone:
        rawData.shipping_phone || rawData.User?.phone_number || "N/A",
      billing_address: fullAddressCompiled || "N/A",
      shipping_fee: shippingFee,
      discount_amount: rawData.discount_amount || 0,
      grand_total: rawData.total_amount,
      total_taxable_value: totalTaxable.toFixed(2),
      total_cgst: totalCGST.toFixed(2),
      total_sgst: totalSGST.toFixed(2),
      total_igst: totalIGST.toFixed(2),
      items: mappedItems,
    };
  }

  // ================= WATERMARK =================
  doc.setFontSize(28);
  doc.setTextColor(240, 243, 248);
  doc.setFont("helvetica", "bold");
  doc.text("ZYNVENTICS TECHNOLOGIES", pageWidth / 2, pageHeight / 2, {
    align: "center",
    baseline: "middle",
    angle: 45,
  });

  // ================= COMPANY CONFIG =================
  const COMPANY = {
    name: "Zynventics Technologies Pvt. Ltd.",
    address1: "Near Youniform world Gobordanga Station road,",
    address2: "Ramnagar, North 24 pargana, 743273, West Bengal",
    gstin: "19AACCZ9658C1ZU",
    pan: "AACCZ9658C",
    cin: "U31900WB2026PTC123456",
    phone: "+91 8902955624",
    email: "info@zynventics.com",
  };

  // ================= HEADER =================
  let currentY = 22;

  doc.setFontSize(16);
  doc.setTextColor(15, 23, 42);
  doc.setFont("helvetica", "bold");
  doc.text(COMPANY.name, 14, currentY);

  currentY += 5;
  doc.setFontSize(9);
  doc.setTextColor(71, 85, 105);
  doc.setFont("helvetica", "normal");
  doc.text(COMPANY.address1, 14, currentY);

  currentY += 5;
  doc.text(COMPANY.address2, 14, currentY);

  currentY += 5;
  doc.text(`Email: ${COMPANY.email} | Phone: ${COMPANY.phone}`, 14, currentY);

  currentY += 5;
  doc.setFont("helvetica", "bold");
  doc.text(`GSTIN: ${COMPANY.gstin} | PAN: ${COMPANY.pan}`, 14, currentY);

  doc.setFontSize(22);
  doc.setTextColor(37, 99, 235);
  doc.setFont("helvetica", "bold");
  doc.text("TAX INVOICE", pageWidth - 14, 24, { align: "right" });

  doc.setFontSize(9);
  doc.setTextColor(15, 23, 42);
  doc.text(
    `Invoice No: ${String(invoiceRecord.invoice_number || rawData.invoice_number)}`,
    pageWidth - 14,
    32,
    { align: "right" },
  );

  const stateCode = isInterState ? "N/A" : "19";

  doc.setFont("helvetica", "bold");
  doc.text(
    `Place of Supply: ${String(determinedPlaceOfSupply)} (${stateCode})`,
    pageWidth - 14,
    37,
    { align: "right" },
  );
  doc.text("Reverse Charge: No", pageWidth - 14, 42, { align: "right" });

  doc.setDrawColor(200, 200, 200);
  doc.line(14, 48, pageWidth - 14, 48);

  // ================= DETAILS SECTION =================
  let detailsY = 53;

  let rawPhone = String(invoiceRecord.customer_phone || "").trim();
  let formattedPhone = "N/A";
  if (rawPhone && rawPhone !== "undefined" && rawPhone !== "null") {
    const digitsOnly = rawPhone.replace(/\D/g, "");
    if (digitsOnly.length === 10) formattedPhone = `+91 ${digitsOnly}`;
    else if (digitsOnly.length === 12 && digitsOnly.startsWith("91"))
      formattedPhone = `+91 ${digitsOnly.slice(2)}`;
    else formattedPhone = digitsOnly;
  }

  doc.setFontSize(9);
  doc.setTextColor(15, 23, 42);

  doc.setFont("helvetica", "bold");
  doc.text("Order ID:", 14, detailsY);
  doc.setFont("helvetica", "normal");

  // ✅ UPDATED: Printed Order ID using formatOrderId
  doc.text(formatOrderId(invoiceRecord.order_id), 14, detailsY + 5);

  const orderDateStr = new Date(
    invoiceRecord.invoice_date || rawData.createdAt || new Date(),
  )
    .toLocaleDateString("en-IN")
    .replace(/\//g, "-");

  doc.setFont("helvetica", "bold");
  doc.text("Order Date:", 14, detailsY + 12);
  doc.setFont("helvetica", "normal");
  doc.text(orderDateStr, 34, detailsY + 12);

  doc.setFont("helvetica", "bold");
  doc.text("Invoice Date:", 14, detailsY + 17);
  doc.setFont("helvetica", "normal");
  doc.text(orderDateStr, 36, detailsY + 17);

  doc.setFont("helvetica", "bold");
  doc.text("PAN:", 14, detailsY + 22);
  doc.setFont("helvetica", "normal");
  doc.text(COMPANY.pan, 23, detailsY + 22);

  doc.setFont("helvetica", "bold");
  doc.text("CIN:", 14, detailsY + 27);
  doc.setFont("helvetica", "normal");
  doc.text(COMPANY.cin, 22, detailsY + 27);

  const col2X = 64;
  const addressWidth = 45;

  doc.setFont("helvetica", "bold");
  doc.text("Bill To", col2X, detailsY);
  doc.text(
    String(invoiceRecord.customer_name || rawData.customer_name),
    col2X,
    detailsY + 5,
  );

  doc.setFont("helvetica", "normal");
  const billAddressLines = doc.splitTextToSize(
    String(invoiceRecord.billing_address || rawData.billing_address || "N/A"),
    addressWidth,
  );
  doc.text(billAddressLines, col2X, detailsY + 10);
  const billAddressHeight = billAddressLines.length * 4;
  doc.text(
    `Phone: ${formattedPhone}`,
    col2X,
    detailsY + 10 + billAddressHeight,
  );

  const col3X = 114;
  doc.setFont("helvetica", "bold");
  doc.text("Ship To", col3X, detailsY);
  doc.text(
    String(invoiceRecord.customer_name || rawData.customer_name),
    col3X,
    detailsY + 5,
  );

  doc.setFont("helvetica", "normal");
  const shipAddressLines = doc.splitTextToSize(
    String(invoiceRecord.billing_address || rawData.billing_address || "N/A"),
    addressWidth,
  );
  doc.text(shipAddressLines, col3X, detailsY + 10);
  const shipAddressHeight = shipAddressLines.length * 4;
  doc.text(
    `Phone: ${formattedPhone}`,
    col3X,
    detailsY + 10 + shipAddressHeight,
  );

  const col4X = 164;
  doc.setFont("helvetica", "italic");
  const noteText =
    "*Keep this invoice and manufacturer box for warranty purposes.";
  const noteLines = doc.splitTextToSize(noteText, 32);
  doc.text(noteLines, col4X, detailsY + 10);

  const maxAddressLines = Math.max(
    billAddressLines.length,
    shipAddressLines.length,
  );
  const tableStartY = detailsY + Math.max(27, 10 + maxAddressLines * 4 + 6) + 6;

  // ================= DYNAMIC ITEMS TABLE =================
  const tableColumn = [
    "Product Details",
    { content: "Qty", styles: { halign: "center" } },
    { content: "Unit Price\n(Excl)", styles: { halign: "right" } },
    { content: "Taxable", styles: { halign: "right" } },
  ];

  if (isInterState) {
    tableColumn.push({ content: "IGST", styles: { halign: "right" } });
  } else {
    tableColumn.push({ content: "CGST", styles: { halign: "right" } });
    tableColumn.push({ content: "SGST", styles: { halign: "right" } });
  }

  tableColumn.push({ content: "Total", styles: { halign: "right" } });

  const tableRows = [];

  invoiceRecord.items.forEach((item) => {
    const actualGstStored =
      Number(item.igst || 0) + Number(item.cgst || 0) + Number(item.sgst || 0);
    const fallbackGstAmt = Number(item.taxable) * (Number(item.gst_rate) / 100);
    const rowGst = actualGstStored > 0 ? actualGstStored : fallbackGstAmt;

    const row = [
      `${item.name}\nSKU: ${item.sku || "N/A"}\nHSN: ${item.hsn || "N/A"}`,
      item.qty,
      item.unit_price,
      item.taxable,
    ];

    if (isInterState) {
      row.push(`${rowGst.toFixed(2)}\n(${item.gst_rate}%)`);
    } else {
      const halfGst = (rowGst / 2).toFixed(2);
      row.push(`${halfGst}\n(${item.gst_rate / 2}%)`);
      row.push(`${halfGst}\n(${item.gst_rate / 2}%)`);
    }

    row.push(item.total);
    tableRows.push(row);
  });

  if (Number(invoiceRecord.shipping_fee) > 0) {
    const shipTaxable = (Number(invoiceRecord.shipping_fee) / 1.18).toFixed(2);
    const shipGstAmt = (
      Number(invoiceRecord.shipping_fee) - shipTaxable
    ).toFixed(2);
    const shipHalfGst = (shipGstAmt / 2).toFixed(2);

    const shippingRow = ["Shipping\nHSN: 996812", 1, shipTaxable, shipTaxable];

    if (isInterState) {
      shippingRow.push(`${shipGstAmt}\n(18%)`);
    } else {
      shippingRow.push(`${shipHalfGst}\n(9%)`);
      shippingRow.push(`${shipHalfGst}\n(9%)`);
    }

    shippingRow.push(Number(invoiceRecord.shipping_fee).toFixed(2));
    tableRows.push(shippingRow);
  }

  const dynamicColumnStyles = {
    0: { cellWidth: 55 },
    1: { halign: "center" },
    2: { halign: "right" },
    3: { halign: "right" },
  };

  if (isInterState) {
    dynamicColumnStyles[4] = { halign: "right" };
    dynamicColumnStyles[5] = { halign: "right", fontStyle: "bold" };
  } else {
    dynamicColumnStyles[4] = { halign: "right" };
    dynamicColumnStyles[5] = { halign: "right" };
    dynamicColumnStyles[6] = { halign: "right", fontStyle: "bold" };
  }

  autoTable(doc, {
    startY: tableStartY,
    head: [tableColumn],
    body: tableRows,
    theme: "striped",
    headStyles: { fillColor: [37, 99, 235], textColor: [255, 255, 255] },
    columnStyles: dynamicColumnStyles,
  });

  // ================= DYNAMIC TOTALS =================
  let finalY = doc.lastAutoTable.finalY + 10;
  const labelX = pageWidth - 45;
  const valueX = pageWidth - 14;

  doc.setFontSize(9);
  doc.setTextColor(71, 85, 105);

  doc.text("Taxable Value:", labelX, finalY, { align: "right" });
  doc.text(`Rs. ${invoiceRecord.total_taxable_value}`, valueX, finalY, {
    align: "right",
  });

  let cursorY = finalY;

  const finalTotalGst =
    Number(invoiceRecord.total_igst || 0) +
    Number(invoiceRecord.total_cgst || 0) +
    Number(invoiceRecord.total_sgst || 0);

  if (isInterState) {
    cursorY += 6;
    doc.text("Total IGST:", labelX, cursorY, { align: "right" });
    doc.text(`Rs. ${finalTotalGst.toFixed(2)}`, valueX, cursorY, {
      align: "right",
    });
  } else {
    cursorY += 6;
    doc.text("Total CGST:", labelX, cursorY, { align: "right" });
    doc.text(`Rs. ${(finalTotalGst / 2).toFixed(2)}`, valueX, cursorY, {
      align: "right",
    });

    cursorY += 6;
    doc.text("Total SGST:", labelX, cursorY, { align: "right" });
    doc.text(`Rs. ${(finalTotalGst / 2).toFixed(2)}`, valueX, cursorY, {
      align: "right",
    });
  }

  if (Number(invoiceRecord.discount_amount) > 0) {
    cursorY += 6;
    doc.setTextColor(220, 38, 38);
    doc.text(`Discount:`, labelX, cursorY, { align: "right" });
    doc.text(`- Rs. ${invoiceRecord.discount_amount}`, valueX, cursorY, {
      align: "right",
    });
  }

  cursorY += 8;
  doc.setFillColor(241, 245, 249);
  doc.rect(pageWidth - 80, cursorY - 6, 66, 12, "F");

  doc.setFontSize(11);
  doc.setTextColor(15, 23, 42);
  doc.setFont("helvetica", "bold");
  doc.text("Grand Total:", labelX, cursorY + 2, { align: "right" });
  doc.text(
    `Rs. ${Number(invoiceRecord.grand_total).toLocaleString("en-IN")}`,
    valueX,
    cursorY + 2,
    { align: "right" },
  );

  // ================= TERMS & DECLARATION =================
  let policyY = cursorY + 20;

  doc.setFontSize(9);
  doc.setTextColor(15, 23, 42);
  doc.setFont("helvetica", "bold");
  doc.text("Terms & Conditions / Return Policy:", 14, policyY);

  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(100, 116, 139);

  const policies = [
    "1. Return & Refund: Items can be returned within 2 days of delivery. Original packaging and invoice are required.",
    "2. Warranty: 1 Year replacement warranty on battery and 2 years of service warranty on battery packs. Physical damage or tampering voids the warranty.",
    "3. Goods sold are intended for end-user consumption. All disputes are subject to West Bengal jurisdiction. E. & O.E.",
  ];

  policies.forEach((policy) => {
    policyY += 4;
    const splitPolicy = doc.splitTextToSize(policy, pageWidth - 90);
    doc.text(splitPolicy, 14, policyY);
    policyY += (splitPolicy.length - 1) * 3;
  });

  policyY += 12;
  doc.setFontSize(8);
  doc.setTextColor(15, 23, 42);
  doc.text(
    "Certified that the particulars given above are true and correct.",
    14,
    policyY,
  );

  doc.setFont("helvetica", "bold");
  doc.text(
    "For Zynventics Technologies Pvt. Ltd.",
    pageWidth - 14,
    policyY - 5,
    { align: "right" },
  );

  doc.setFont("helvetica", "normal");
  doc.text("Authorized Signatory", pageWidth - 14, policyY + 5, {
    align: "right",
  });

  doc.save(
    `Invoice_${String(invoiceRecord.invoice_number || rawData.invoice_number).replace(/\//g, "_")}.pdf`,
  );
};
